import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getFilteredRowModel,
  createColumnHelper,
  Table as TableType,
} from '@tanstack/react-table';

// Mocks

// Types
import {
  TableProps,
  dataTable,
  SearchbarFilterTypes,
  materialsColumnNames,
} from './types';

// Styled components
import {
  ActionButton,
  ActionCell,
  ContainerTable,
  FiltersContainer,
  MaterialNameCell,
  InactiveMaterialsTableContainer,
  MaterialsTableContainer,
  SubCodificationCell,
  SwapMaterialButton,
  SwapMaterialButtonRight,
  Tag,
  TagsContainer,
  TrStyled,
} from './styles';

// Icons
import EditIcon from '../../../../../../assets/icons/edit-icon.svg';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
} from '../../../summaryTables/SummaryTablesStyles';
import {
  InputRadioContainer,
  InputRadioStyled,
} from '../../../components/MainStyledComponents';
import { Typography } from '../../../../Typography';

import { materialsMocksData } from './mocks';
import SearchBar from '../../../../../inputs/SearchBar';
import { matchesSearch } from '../../../../../../utils/textSearch';

/**
 * Catálogo de materiales del módulo de Gestión.
 *
 * **Dos listas apiladas** (activos arriba, inactivos abajo) con selección por
 * radio y flechas ⮕ / ⬅ para moverlas de una a otra — la mecánica de
 * `ExternalCompaniesManagementTable` y `UsersManagementTable`.
 *
 * 🔴 **Apiladas, no en fila**, y ahí diverge del original a propósito: aquellas
 * tienen 3-4 columnas y caben a media pantalla; esta tiene **siete** (código,
 * material, codificación, subcodificación, unidad, equipos y acciones). En fila
 * quedarían catorce columnas repartidas en dos mitades, ilegibles en la tablet
 * que es el objetivo de la app. Es el mismo patrón adaptado al número de
 * columnas, no otro patrón.
 *
 * Sin `onDeactivate`/`onActivate` la tabla se comporta como antes: una sola
 * lista con todo el catálogo y sin columna de selección.
 */
export const MaterialsManagementTable = ({
  data,
  onEditMaterialTeams,
  onDeactivate,
  onActivate,
}: TableProps): JSX.Element => {
  const { t } = useTranslation(['forms', 'pages']);
  // `materialsMocksData` es el respaldo de la story: se tipa como fila real
  // para que el reparto por estado no dependa de su forma.
  const materials: dataTable[] = data?.materials ?? (materialsMocksData as dataTable[]);

  // La baja lógica solo se ofrece si el llamador sabe ejecutarla.
  const supportsActivation = Boolean(onDeactivate && onActivate);

  const activeMaterials = useMemo(
    () => data?.activeMaterials ?? materials.filter((m) => m.isActive !== false),
    [data?.activeMaterials, materials]
  );
  const inactiveMaterials = useMemo(
    () => data?.inactiveMaterials ?? [],
    [data?.inactiveMaterials]
  );

  const [materialsData, setMaterialsData] = useState(activeMaterials);
  const [inactiveData, setInactiveData] = useState(inactiveMaterials);
  const [activeChecked, setActiveChecked] = useState({});
  const [inactiveChecked, setInactiveChecked] = useState({});
  const [filteredMaterials, setFilteredMaterials] = useState(false);

  const materialsColumnHelper = createColumnHelper<dataTable>();

  useEffect(() => {
    setMaterialsData(activeMaterials);
    setInactiveData(inactiveMaterials);
  }, [activeMaterials, inactiveMaterials]);

  const materialsColumns = useMemo(
    () => [
      // La columna de selección solo existe si se puede mover la fila.
      ...(supportsActivation
        ? [
            materialsColumnHelper.display({
              id: 'select',
              header: () => '',
              cell: ({ row }) => (
                <InputRadioContainer>
                  <InputRadioStyled
                    name={`materialRadio-${row.original.isActive === false ? 'inactive' : 'active'}`}
                    {...{
                      checked: row.getIsSelected(),
                      disabled: !row.getCanSelect(),
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler(),
                    }}
                  />
                </InputRadioContainer>
              ),
            }),
          ]
        : []),
      materialsColumnHelper.accessor((row) => row.materialId, {
        id: materialsColumnNames.materialId.id,
        header: () => t('management.materialTableHeaders.materialId'),
        cell: (info) => info.getValue(),
      }),
      materialsColumnHelper.accessor((row) => row.name, {
        id: materialsColumnNames.name.id,
        header: () => t('management.materialTableHeaders.name'),
        cell: (info) => {
          const name = (info.getValue() as string) ?? '';
          return (
            <MaterialNameCell size={'S'} title={name}>
              {name}
            </MaterialNameCell>
          );
        },
      }),
      materialsColumnHelper.accessor((row) => row.codification, {
        id: materialsColumnNames.codification.id,
        header: () => t('management.materialTableHeaders.codification'),
      }),
      materialsColumnHelper.accessor((row) => row.subCodification, {
        id: materialsColumnNames.subCodification.id,
        header: () => t('management.materialTableHeaders.subCodification'),
        cell: (info) => {
          const sub = (info.getValue() as string) ?? '';
          return (
            <SubCodificationCell size={'S'} title={sub}>
              {sub}
            </SubCodificationCell>
          );
        },
      }),
      materialsColumnHelper.accessor((row) => row.typeOfMeasurement, {
        id: materialsColumnNames.typeOfMeasurement.id,
        header: () => t('management.materialTableHeaders.typeOfMeasurement'),
      }),
      // Materiales por equipo (2026-07-31). Sin equipos se pinta "Todos" y no
      // un guion: la semántica "está disponible para todo el mundo" tiene que
      // leerse de un vistazo, no parecer un dato que falta.
      materialsColumnHelper.accessor((row) => row.teamNames, {
        id: materialsColumnNames.teams.id,
        header: () => t('management.materialTableHeaders.teams'),
        cell: (info) => {
          const names = info.getValue();
          if (!names || names.length === 0) {
            return <Tag>{t('management.materialTeams.allTeams')}</Tag>;
          }
          return (
            <TagsContainer>
              {names.slice(0, 3).map((name: string, idx: number) => (
                <Tag key={idx}>{name}</Tag>
              ))}
              {names.length > 3 && <Tag>+{names.length - 3}</Tag>}
            </TagsContainer>
          );
        },
      }),
      materialsColumnHelper.display({
        id: 'actions',
        header: () => '',
        cell: ({ row }) => (
          <ActionCell>
            <ActionButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditMaterialTeams?.(row.original);
              }}
              title={t('management.materialTeams.editTitle')}
            >
              <img
                src={EditIcon}
                alt={t('management.materialTeams.editTitle')}
                style={{ width: '1.8rem', height: '1.8rem' }}
              />
            </ActionButton>
          </ActionCell>
        ),
      }),
    ],
    [materialsData, data, t, onEditMaterialTeams, supportsActivation]
  );

  const materialsSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      if (value === '') {
        setMaterialsData(activeMaterials);
        setFilteredMaterials((prev) => !prev);
      } else {
        const filtered = activeMaterials.filter(
          (material) =>
            matchesSearch(
              [
                material.materialId,
                material.name,
                material.codification,
                material.subCodification,
              ],
              value
            )
        );
        setMaterialsData(filtered);
      }
    },
    [activeMaterials, filteredMaterials]
  );

  const inactiveSearchFilter = useCallback<SearchbarFilterTypes>(
    (value) => {
      setInactiveData(
        value === ''
          ? inactiveMaterials
          : inactiveMaterials.filter(
              (material) =>
                matchesSearch(
              [
                material.materialId,
                material.name,
                material.codification,
                material.subCodification,
              ],
              value
            )
            )
      );
    },
    [inactiveMaterials]
  );

  const activeTable = useReactTable({
    getRowId: (row) => row.id,
    data: materialsData,
    columns: materialsColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setActiveChecked,
    state: { rowSelection: activeChecked },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  const inactiveTable = useReactTable({
    getRowId: (row) => row.id,
    data: inactiveData,
    columns: materialsColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setInactiveChecked,
    state: { rowSelection: inactiveChecked },
    enableRowSelection: true,
    enableMultiRowSelection: false,
  });

  const renderTable = (table: TableType<dataTable>) => (
    <TableContainer>
      <Table>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Th>
              ))}
            </tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <TrStyled
              key={row.id}
              checked={row.getIsSelected()}
              indeterminate={!table.getIsSomeRowsSelected()}
              onClick={
                supportsActivation
                  ? () => row.getToggleSelectedHandler()(row)
                  : undefined
              }
            >
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </TrStyled>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );

  /**
   * Mueve la fila seleccionada de una lista a la otra.
   *
   * La selección se limpia siempre: la fila deja de estar en esta tabla, y
   * dejar el radio marcado sobre una fila que ya no existe engaña.
   */
  const moveSelected = (
    table: TableType<dataTable>,
    action?: (id: string) => void
  ) => {
    const selected = table.getSelectedRowModel().rows[0];
    if (!selected || !action) return;
    action(selected.original.id);
    table.resetRowSelection(true);
  };

  return (
    <ContainerTable>
      <MaterialsTableContainer>
        <Typography size="L" variant="semiBold">
          {supportsActivation
            ? `${t('pages:management.activeMaterials')} (${materialsData.length})`
            : t('pages:management.materials')}
        </Typography>
        <FiltersContainer>
          <SearchBar
            variant="form"
            onChangeInput={(e) => materialsSearchFilter(e.target.value)}
          />
        </FiltersContainer>
        {renderTable(activeTable)}
        {supportsActivation && (
          <SwapMaterialButtonRight
            type="button"
            title={t('pages:management.deactivate')}
            disabled={activeTable.getSelectedRowModel().rows.length === 0}
            onClick={() => moveSelected(activeTable, onDeactivate)}
          >
            ⬇
          </SwapMaterialButtonRight>
        )}
      </MaterialsTableContainer>

      {supportsActivation && (
        <InactiveMaterialsTableContainer>
          <Typography size="L" variant="semiBold">
            {`${t('pages:management.inactiveMaterials')} (${inactiveData.length})`}
          </Typography>
          <FiltersContainer>
            <SearchBar
              variant="form"
              onChangeInput={(e) => inactiveSearchFilter(e.target.value)}
            />
          </FiltersContainer>
          {renderTable(inactiveTable)}
          <SwapMaterialButton
            type="button"
            title={t('pages:management.activate')}
            disabled={inactiveTable.getSelectedRowModel().rows.length === 0}
            onClick={() => moveSelected(inactiveTable, onActivate)}
          >
            ⬆
          </SwapMaterialButton>
        </InactiveMaterialsTableContainer>
      )}
    </ContainerTable>
  );
};
